import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ProjectDetails, GalleryItem } from './types';
import Header from '../../components/UI/Header';
import ProjectBreadcrumb from '../../components/UI/ProjectBreadcrumb';
import ProjectHeader from './components/ProjectHeader';
import ProjectNav from './components/ProjectNav';
import ProjectOverview from './components/ProjectOverview';
import TechnicalFeatures from './components/TechnicalFeatures';
import MediaGallery from './components/MediaGallery';
import DevelopmentTimeline from './components/DevelopmentTimeline';
import ProjectResults from './components/ProjectResult';
import ChallengesSolutions from './components/ChallengesSolutions';
import RelatedProjects from './components/RelatedProject';
import Button from '../../components/UI/Button';
import Icon from '../../components/AppIcon';
import { supabase } from '../../lib/supabase';
import Footer from '../../components/UI/Footer';
import { Project } from '../../types';

const ProjectDetailsPage = () => {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('id');
  const [project, setProject] = useState<ProjectDetails | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }

    const fetchProject = async () => {
      try {
        const { data, error } = await supabase
          .from('projects')
          .select('*')
          .eq('id', projectId)
          .single();

        if (error) throw error;

        if (data) {
          const dbProject = data as Project;
          // Map DB Project to ProjectDetails UI type
          const mappedProject: ProjectDetails = {
            id: dbProject.id,
            title: dbProject.title,
            subtitle: dbProject.subtitle || '',
            completionDate: dbProject.details?.completionDate || 'N/A',
            client: dbProject.details?.client || 'Personal Project',
            duration: dbProject.details?.duration || '',
            status: (dbProject.details?.status as any) || 'completed',
            categories: (Array.isArray(dbProject.details?.categories) ? dbProject.details.categories : (dbProject.details?.category ? [dbProject.details.category] : ['Project'])),
            description: dbProject.description || '',
            image: dbProject.image_url || '',
            imageAlt: dbProject.title,
            technologies: (dbProject.tags || []).map(tag => ({
              id: tag.toLowerCase().replace(/\s+/g, '-'),
              name: tag,
            })),
            links: {
              liveDemo: dbProject.live_link || undefined,
              repository: dbProject.github_link || undefined,
              figma: dbProject.details?.links?.figma || undefined,
            },
            overview: {
              subtitle: dbProject.subtitle || '',
              description: dbProject.description || '',
              challenge: dbProject.details?.overview?.challenge || '',
              solution: dbProject.details?.overview?.solution || '',
              targetAudience: dbProject.details?.overview?.targetAudience || '',
              keyFeatures: dbProject.details?.keyFeatures || '',
              myRole: dbProject.details?.myRole || '',
              achievement: dbProject.details?.achievement || '',
            },
            features: dbProject.details?.features || [],
            gallery: (() => {
              const mainImg = dbProject.image_url ? {
                id: 'main',
                src: dbProject.image_url,
                type: 'image' as const,
                alt: dbProject.title,
                title: 'Project Preview',
                category: 'desktop',
                thumbnail: dbProject.image_url
              } : null;

              const additionalGallery = (dbProject.details?.gallery as any[])?.map(item => ({
                ...item,
                type: item.type || 'image',
                thumbnail: item.thumbnail || item.src,
                category: item.category || 'project'
              })) || [];

              if (mainImg) {
                return [mainImg, ...additionalGallery];
              }
              return additionalGallery;
            })(),
            timeline: dbProject.details?.timeline || [],
            results: dbProject.details?.results || {
              summary: '',
              metrics: [],
              achievements: []
            },
            challenges: dbProject.details?.challenges || [],
            demoCredentials: dbProject.details?.demoCredentials || undefined,
            testimonials: dbProject.details?.testimonials || [],
            relatedProjects: [] // Could be fetched separately if needed
          };
          setProject(mappedProject);
        }
      } catch (err) {
        console.error('Error fetching project:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [projectId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF9EB]">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-[#0C2C39]/10 border-t-[#0D9488] rounded-full animate-spin mx-auto mb-6"></div>
              <p className="text-[#0C2C39]/40 font-black uppercase tracking-widest text-xs">Initializing Project Data</p>
            </div>
          </div>
        </div>
      </div>);
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-[#FFF9EB]">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="text-center py-16 bg-white border-2 border-[#0C2C39] rounded-3xl p-12 shadow-[12px_12px_0px_0px_#0C2C39]">
            <Icon name="AlertCircle" size={64} className="text-[#0C2C39]/20 mx-auto mb-6" />
            <h1 className="text-4xl font-black text-[#0C2C39] mb-4 uppercase tracking-tighter">Project Not Found</h1>
            <p className="text-lg text-[#0C2C39]/60 mb-8 font-medium">The requested project architecture could not be located in the repository.</p>
            <Link to="/projects-showcase">
              <Button className="bg-[#0C2C39] text-white hover:bg-[#0D9488] px-10 py-4 rounded-xl font-black uppercase tracking-widest text-xs transition-all">
                <Icon name="ArrowLeft" size={18} />
                Back to Showcase
              </Button>
            </Link>
          </div>
        </div>
      </div>);
  }

  // Compute which sections have data — used for dynamic navigation
  const visibleSections: string[] = [];
  if (project.overview.description || project.overview.challenge || project.overview.solution || project.overview.keyFeatures || project.overview.myRole || project.overview.achievement || project.overview.targetAudience) {
    visibleSections.push('overview');
  }
  if (project.gallery.length > 0) visibleSections.push('gallery');
  if (project.features.length > 0) visibleSections.push('features');
  if (project.timeline.length > 0) visibleSections.push('timeline');
  if (project.results.summary || project.results.metrics.length > 0 || project.results.achievements.length > 0) visibleSections.push('results');
  if (project.challenges.length > 0) visibleSections.push('challenges');

  return (
    <div className="min-h-screen bg-[#FFF9EB]">
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24">
        {/* ─── Breadcrumb ─── */}
        <ProjectBreadcrumb
          projectTitle={project.title}
          projectId={project.id}
          className="mb-12"
        />

        {/* ─── Hero Header ─── */}
        <ProjectHeader project={project} />

        {/* ─── Sticky Navigation (only shows existing sections) ─── */}
        <ProjectNav sections={visibleSections} />

        {/* ─── Content Sections ─── */}
        <div className="mt-24 space-y-32">
          {/* Overview — show when there's any overview data */}
          {(project.overview.description || project.overview.challenge || project.overview.solution || project.overview.keyFeatures || project.overview.myRole || project.overview.achievement || project.overview.targetAudience) && (
            <div id="overview">
              <ProjectOverview overview={project.overview} />
            </div>
          )}

          {/* Features — only show if admin added features */}
          {project.features.length > 0 && (
            <div id="features">
              <TechnicalFeatures features={project.features} />
            </div>
          )}

          {/* Gallery — only show if there are images */}
          {project.gallery.length > 0 && (
            <div id="gallery">
              <MediaGallery items={project.gallery} />
            </div>
          )}

          {/* Timeline — only show if admin added timeline items */}
          {project.timeline.length > 0 && (
            <div id="timeline">
              <DevelopmentTimeline timeline={project.timeline} />
            </div>
          )}

          {/* Results — only show if there's a summary or metrics */}
          {(project.results.summary || project.results.metrics.length > 0 || project.results.achievements.length > 0) && (
            <div id="results">
              <ProjectResults results={project.results} />
            </div>
          )}

          {/* Challenges — only show if admin added challenges */}
          {project.challenges.length > 0 && (
            <div id="challenges">
              <ChallengesSolutions challenges={project.challenges} />
            </div>
          )}
        </div>

        {/* Demo Credentials Section */}
        {project.demoCredentials && (project.demoCredentials.user || project.demoCredentials.password) && (
          <section className="bg-[#0C2C39] text-white rounded-[2.5rem] p-12 mt-32 relative overflow-hidden group shadow-2xl">
            <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:opacity-10 transition-opacity">
              <Icon name="Lock" size={160} />
            </div>

            <div className="relative z-10 text-center md:text-left">
              <div className="flex flex-col md:flex-row items-center gap-6 mb-10">
                <div className="w-16 h-16 bg-[#0D9488] flex items-center justify-center rounded-2xl">
                  <Icon name="ShieldCheck" size={32} className="text-white" />
                </div>
                <div>
                  <h2 className="text-3xl font-black text-white tracking-tight uppercase">Demo Access Protocol</h2>
                  <p className="text-slate-400 font-medium italic">Use these coordinates to explore the live application environment.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {project.demoCredentials.user && (
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="text-center md:text-left">
                      <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Identity / Email</p>
                      <p className="text-white font-black text-lg select-all">{project.demoCredentials.user}</p>
                    </div>
                    <button
                      onClick={() => navigator.clipboard.writeText(project.demoCredentials?.user || '')}
                      className="p-3 bg-white/5 hover:bg-white/10 rounded-xl text-white transition-all active:scale-95"
                    >
                      <Icon name="Copy" size={20} />
                    </button>
                  </div>
                )}
                {project.demoCredentials.password && (
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="text-center md:text-left">
                      <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Secure Code</p>
                      <p className="text-white font-black text-lg select-all">{project.demoCredentials.password}</p>
                    </div>
                    <button
                      onClick={() => navigator.clipboard.writeText(project.demoCredentials?.password || '')}
                      className="p-3 bg-white/5 hover:bg-white/10 rounded-xl text-white transition-all active:scale-95"
                    >
                      <Icon name="Copy" size={20} />
                    </button>
                  </div>
                )}
              </div>

              {project.links.liveDemo && (
                <div className="mt-10 flex justify-center md:justify-start">
                  <a
                    href={project.links.liveDemo}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-4 bg-[#0D9488] text-white px-10 py-5 rounded-2xl font-black uppercase tracking-widest transition-all shadow-xl shadow-[#0D9488]/20 hover:bg-[#0ea5e9] active:scale-95 text-sm"
                  >
                    Establish Connection
                    <Icon name="ExternalLink" size={20} />
                  </a>
                </div>
              )}
            </div>
          </section>
        )}

        {project.relatedProjects.length > 0 && (
          <div className="mt-32">
            <RelatedProjects projects={project.relatedProjects} />
          </div>
        )}

        {/* Contact CTA */}
        <div className="bg-white border-2 border-[#0C2C39] rounded-[2.5rem] p-12 text-center shadow-[16px_16px_0px_0px_#0C2C39] mt-32">
          <h2 className="text-4xl font-black text-[#0C2C39] mb-4 tracking-tighter uppercase">Interested in Similar Architecture?</h2>
          <p className="text-xl text-[#0C2C39]/60 mb-10 max-w-2xl mx-auto font-medium leading-relaxed">
            Let's discuss how I can help bring your project ideas to life with the same attention to detail and technical excellence.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/contact-form">
              <Button className="w-full sm:w-auto bg-[#0C2C39] text-white hover:bg-[#0D9488] py-5 px-12 rounded-2xl font-black uppercase tracking-widest transition-all text-xs shadow-xl shadow-[#0C2C39]/10">
                <Icon name="Mail" size={20} className="mr-3" />
                Start a Conversation
              </Button>
            </Link>
            <Link to="/projects-showcase">
              <Button variant="outline" className="w-full sm:w-auto border-2 border-[#0C2C39] text-[#0C2C39] hover:bg-[#0C2C39] hover:text-white py-5 px-12 rounded-2xl font-black uppercase tracking-widest text-xs transition-all">
                <Icon name="FolderOpen" size={20} className="mr-3" />
                View More Projects
              </Button>
            </Link>
          </div>
        </div>
      </main>

      <Footer />

      {/* Floating Action Buttons for Mobile */}
      <div className="fixed bottom-8 right-8 flex flex-col gap-4 lg:hidden z-50">
        {project.links.liveDemo &&
          <button
            className="w-14 h-14 bg-[#0D9488] text-white rounded-full shadow-2xl flex items-center justify-center transition-transform active:scale-95"
            onClick={() => window.open(project.links.liveDemo, '_blank')}
          >
            <Icon name="ExternalLink" size={24} />
          </button>
        }

        {project.links.repository &&
          <button
            className="w-14 h-14 bg-[#0C2C39] text-white rounded-full shadow-2xl flex items-center justify-center transition-transform active:scale-95"
            onClick={() => window.open(project.links.repository, '_blank')}
          >
            <Icon name="Github" size={24} />
          </button>
        }
      </div>
    </div>
  );
};

export default ProjectDetailsPage;